import { useEffect, useState } from 'react'

const DESKTOP_QUERY = '(min-width: 768px)'

/**
 * Returns true when viewport is ≥768px (Tailwind `md:` breakpoint).
 *
 * SSR-safe default is `true` (desktop-first) — the first render on
 * environments without `window` shows desktop layout, and the value
 * reconciles on hydration once matchMedia subscription fires.
 *
 * Used by UserCallPage to pick `variant='desktop'|'mobile'` for the single
 * shared <PhoneCallView>. Must NOT cause remount — callers pass the result
 * as a prop only, never as part of a React `key`.
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true
    }
    return window.matchMedia(DESKTOP_QUERY).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mql = window.matchMedia(DESKTOP_QUERY)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return isDesktop
}
